import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const [expandedItem, setExpandedItem] = useState(null);

  const timeline = [
    {
      year: "2026",
      title: "Expanding African Tech Ecosystem",
      role: "Founder & CEO",
      company: "Multiple Ventures",
      description: "Leading digital transformation initiatives across Nigeria, Cameroon, and South Africa. Scaling mentorship programs and launching new tech education platforms.",
      achievements: [
        "Mentored 150+ young professionals",
        "Launched 3 successful tech startups",
        "Established partnerships with 20+ African businesses"
      ],
      icon: "Rocket"
    },
    {
      year: "2024",
      title: "Ed Pulse Creative Direction",
      role: "Creative Director & Music Producer",
      company: "Ed Pulse Brand",
      description: "Founded Ed Pulse, merging technical expertise with creative vision. Delivering brand identities, visual experiences, and music production services.",
      achievements: [
        "Created 50+ brand identities",
        "Produced 15+ music tracks",
        "Collaborated with 30+ creative professionals"
      ],
      icon: "Palette"
    },
    {
      year: "2022",
      title: "Entrepreneurship & Business Consulting",
      role: "Business Consultant & Mentor",
      company: "Independent Practice",
      description: "Transitioned to full-time entrepreneurship, providing business strategy and digital transformation consulting to startups and established businesses.",
      achievements: [
        "Consulted for 40+ businesses",
        "Achieved 85% client success rate",
        "Generated ₦50M+ in client revenue"
      ],
      icon: "TrendingUp"
    },
    {
      year: "2020",
      title: "Senior Full-Stack Developer",
      role: "Technical Lead",
      company: "Tech Innovation Hub",
      description: "Led development teams in building enterprise-scale applications. Specialized in React, Node.js, and cloud architecture solutions.",
      achievements: [
        "Delivered 25+ enterprise projects",
        "Reduced deployment time by 60%",
        "Mentored 20+ junior developers"
      ],
      icon: "Code2"
    },
    {
      year: "2018",
      title: "IT Engineering Career Launch",
      role: "Software Developer",
      company: "Digital Solutions Ltd",
      description: "Started professional journey as a software developer, focusing on web applications and mobile development using modern technologies.",
      achievements: [
        "Built 15+ web applications",
        "Achieved 98% client satisfaction",
        "Earned multiple technical certifications"
      ],
      icon: "Zap"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Clock" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Journey</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Career Timeline</h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            A chronological journey through my professional evolution and key milestones
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border transform -translate-x-1/2" />

            {timeline?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className={`relative mb-8 lg:mb-12 ${index % 2 === 0 ? 'lg:pr-1/2' : 'lg:pl-1/2 lg:ml-auto'}`}
              >
                <div className="hidden lg:block absolute top-8 w-4 h-4 rounded-full bg-primary border-4 border-card" style={{ [index % 2 === 0 ? 'right' : 'left']: '-8px' }} />

                <motion.div
                  whileHover={{ y: -4, transition: { duration: 0.3 } }}
                  className="bg-background rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-border hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl md:text-3xl font-bold text-primary">{item?.year}</span>
                        <Icon 
                          name={expandedItem === index ? "ChevronUp" : "ChevronDown"} 
                          size={20} 
                          color="var(--color-muted-foreground)" 
                        />
                      </div>
                      <h3 className="text-lg md:text-xl font-semibold mb-1">{item?.title}</h3>
                      <p className="text-sm md:text-base text-muted-foreground mb-1">{item?.role}</p>
                      <p className="text-xs md:text-sm text-primary font-medium">{item?.company}</p>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                    {item?.description}
                  </p>

                  <AnimatePresence>
                    {expandedItem === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 border-t border-border">
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <Icon name="Award" size={16} color="var(--color-primary)" />
                            Key Achievements
                          </h4>
                          <ul className="space-y-2">
                            {item?.achievements?.map((achievement, achIndex) => (
                              <li key={achIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                                <span>{achievement}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;